import { Part, usePartsQuery } from '../../../graphql'

/**
 * Resolves the full `Part` for a part id held in the URL.
 *
 * The part filter only stores an id in the query string, but `PartPicker`
 * renders a label built from the part itself. Looking it up here means the
 * picker still shows what is being filtered on after a back-navigation or a
 * shared link, instead of an active-but-invisible filter.
 *
 * `parts( partIds: [ id ] )` is a root field the cache cannot satisfy, so every
 * lookup is a round trip — `pickedPart` keeps the query for ids that came from
 * the URL rather than blanking the picker out after the user's own click.
 */
export default function useSelectedPart( partId?: string, pickedPart?: Part ): Part | undefined {
	const parsedId = partId ? parseInt( partId, 10 ) : NaN
	const hasValidId = !Number.isNaN( parsedId )
	const isPicked = hasValidId && pickedPart !== undefined && String( pickedPart.id ) === partId

	const { data } = usePartsQuery( {
		variables: { partIds: [ parsedId ] },
		skip: !hasValidId || isPicked,
	} )

	if ( !hasValidId ) {
		return undefined
	}

	if ( isPicked ) {
		return pickedPart
	}

	return data?.parts?.[ 0 ] as Part | undefined
}
